import { useMutation } from '@tanstack/react-query';

import { useHttpClient } from '~/hooks/useHttpClient';
import { useLocale } from '~/hooks/useLocale';
import { type ApiDateParams, timeframeToApiParams } from './studentDataParams';
import { createLogger } from '~/utils/logging';
import { sanitizeTagValue } from '~/utils/instrumentation';
import { ClientError } from '~/errors';

const log = createLogger('useStudentGenerateMessageMutation');

export type IncludeKey = 'praises' | 'warnings' | 'attendance';
export type Timeframe = '24h' | '7d' | '14d' | '30d' | '90d' | 'all';
export type MessageFormat = 'email' | 'phone' | 'summary';

export type StudentGenerateMessageRequest = {
  studentId: string;
  periodId: string;
  periodCreatedAt: string;
  timeframe: Timeframe;
  include: IncludeKey[];
  format: MessageFormat;
};

export type MessageContext = {
  sessions: number;
  praises: number;
  warnings: number;
  absences: number;
};

type StudentGenerateMessageApiRequest = {
  studentId: string;
  periodId: string;
  tz: string;
  include: IncludeKey[];
  format: MessageFormat;
} & ApiDateParams;

type StudentGenerateMessageResponse = {
  message: string;
  sessions?: number;
  praises?: number;
  warnings?: number;
  absences?: number;
};

export function useStudentGenerateMessageMutation() {
  const httpClient = useHttpClient();
  const locale = useLocale();

  const { data, error, mutate, reset, status } = useMutation({
    mutationFn: async (request: StudentGenerateMessageRequest) => {
      const tz = locale?.timeZone ?? '';
      const dateParams = timeframeToApiParams(request.timeframe, request.periodCreatedAt);

      log.debug('Generating student message', {
        studentId: request.studentId,
        periodId: request.periodId,
        timeframe: request.timeframe,
        format: request.format,
      });

      const apiRequest: StudentGenerateMessageApiRequest = {
        studentId: request.studentId,
        periodId: request.periodId,
        tz,
        include: request.include,
        format: request.format,
        ...dateParams,
      };

      const result = await httpClient.post<StudentGenerateMessageResponse>('/report/generateCustomMessage', apiRequest);
      log.info('Generated student message successfully', {
        studentId: request.studentId,
        messageLength: result.message.length,
      });
      return result;
    },
    onError: (error: Error) => {
      if (error instanceof ClientError) {
        // Ignore client errors.
      } else {
        log.error('Failed to generate student message', {
          errorMessage: sanitizeTagValue(error.message),
        });
      }
    },
  });

  const messageContext: MessageContext | undefined =
    data?.sessions !== undefined ||
    data?.praises !== undefined ||
    data?.warnings !== undefined ||
    data?.absences !== undefined
      ? {
          sessions: data.sessions ?? 0,
          praises: data.praises ?? 0,
          warnings: data.warnings ?? 0,
          absences: data.absences ?? 0,
        }
      : undefined;

  return {
    error,
    mutate,
    reset,
    status,
    message: data?.message,
    context: messageContext,
  };
}
